// src/api/projectService.ts
import api from './config';

export interface Project {
  id?: number;
  name: string;
  description: string;
  start_date: string;
  target_end_date: string;
  actual_end_date?: string | null;
  status: string;
  priority: string;
  budget?: number | null;
  owner_id: number;
}

const USE_REAL_API = true; // Add this flag

export const getProjects = async (): Promise<Project[]> => {
  try {
    if (USE_REAL_API) {
      const response = await api.get('/api/projects');
      return response.data.data || response.data;
    }
    
    // Fallback to mock data only if API fails and USE_REAL_API is false
    console.warn('Using mock projects data');
    return [
      // Your existing mock data...
    ];
  } catch (error) {
    console.error('Error fetching projects:', error);
    throw error;
  }
};

export const getProjectById = async (id: number): Promise<Project> => {
  try {
    if (USE_REAL_API) {
      const response = await api.get(`/api/projects/${id}`);
      return response.data.data || response.data;
    }
    
    // Mock fallback
    const projects = await getProjects();
    const project = projects.find(p => p.id === id);
    if (!project) throw new Error('Project not found');
    return project;
  } catch (error) {
    console.error(`Error fetching project ${id}:`, error);
    throw error;
  }
};

export const createProject = async (project: Omit<Project, 'id'>): Promise<Project> => {
  try {
    if (USE_REAL_API) {
      const response = await api.post('/api/projects', project);
      return response.data.data || response.data;
    }
    
    // Mock implementation
    const projects = await getProjects();
    const newId = Math.max(...projects.map(p => p.id || 0)) + 1;
    return { ...project, id: newId } as Project;
  } catch (error) {
    console.error('Error creating project:', error);
    throw error;
  }
};

export const updateProject = async (id: number, project: Partial<Project>): Promise<Project> => {
  try {
    if (USE_REAL_API) {
      const response = await api.put(`/api/projects/${id}`, project);
      return response.data.data || response.data;
    }
    
    // Mock implementation
    const projects = await getProjects();
    const index = projects.findIndex(p => p.id === id);
    if (index === -1) throw new Error('Project not found');
    return { ...projects[index], ...project };
  } catch (error) {
    console.error(`Error updating project ${id}:`, error);
    throw error;
  }
};

export const deleteProject = async (id: number): Promise<boolean> => {
  try {
    if (USE_REAL_API) {
      await api.delete(`/api/projects/${id}`);
      return true;
    }
    
    return true;
  } catch (error) {
    console.error(`Error deleting project ${id}:`, error);
    throw error;
  }
};